<!--
  TODO:
    - Implement infinite scroll
    - Implement selectAll and ellipsedSelection features
    - Prepare populateItems function for custom $attrs['item-value']
-->
<template>
  <v-autocomplete
    ref="autocomplete"
    v-bind="$attrs"
    v-on="$listeners"

    :value="value"
    :items="sortedItems"
    cache-items

    :loading="isLoading"
    :search-input.sync="searchQuery"
    :filter="customFilter"

    @change="() => searchQuery = null"
    @focus="() => fetchData()"
  >

  </v-autocomplete>
</template>

<script>
import { debounce, isNil } from 'lodash';
import api from '@/services/api';

export default {
  name: 'IndexSelector',

  inheritAttrs: false,

  // NOTE: "value" must be set as a prop for v-model to work properly
  // @see (https://github.com/vuejs/vue/issues/8430)
  props: {
    value: {
      type: [String, Number, Array],
      default: null,
    },
  },

  data: () => ({
    searchQuery: null,
    isLoading: false,
    items: [],
  }),

  computed: {
    sortedItems: (vm) => {
      const items = [...vm.items];

      return items.sort((itemA, itemB) => (itemA.shortName > itemB.shortName ? 1 : -1));
    },
  },

  watch: {
    searchQuery(newQuery) {
      if (isNil(newQuery)) {
        return;
      }

      this.fetchData();
    },

    value() {
      this.populateItems(this.value);
    },
  },

  mounted() {
    this.populateItems(this.value);
  },

  methods: {
    customFilter(item, queryText) {
      const query = queryText.toLowerCase();

      const rules = [
        item.name.toLowerCase().includes(query),
        item.ticker?.toLowerCase().includes(query),
        item.tickerBlomberg?.toLowerCase().includes(query),
      ];

      return rules.some(Boolean);
    },

    updateValue(value) {
      this.$emit('input', value);
      this.$emit('change', value);
    },

    fetchData: debounce(async function debouncedFetchData() {
      this.isLoading = true;

      try {
        if (this.searchQuery) {
          const { data } = await api.indexes.search({ query: this.searchQuery });
          this.items = data.indexes ?? [];
        } else {
          const { data } = await api.indexes.list();
          this.items = data.indexes ?? [];
        }
      } catch (error) {
        this.$store.dispatch('alert/showAlert', {
          errorList: [error.response?.data.message ?? error.message],
        });
      } finally {
        this.isLoading = false;
      }
    }, 750),

    async populateItems(selection) {
      if (!selection?.length) {
        return;
      }

      const cachedItems = this.$refs.autocomplete?.cachedItems ?? [];
      const selectedItems = Array.isArray(selection) ? selection : [selection];

      this.isLoading = true;

      try {
        await Promise.all(selectedItems.map(async (id) => {
          if (cachedItems.some((item) => item._id === id)) {
            return;
          }

          const { data } = await api.indexes.byId(id);
          this.items = [data.index];
        }));
      } catch (error) {
        this.$store.dispatch('alert/showAlert', {
          errorList: [error.response?.data.message ?? error.message],
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
